import { convertErrorMarginToNumber } from 'helpers/error-margin.helpers';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { ResponseCategory } from 'services/assist-api/entities/test-instance-response/reports/report-responses';
import type { ResponseStatus } from 'services/assist-api/entities/test-instance-response/types';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';
import { getBatchedQueries } from 'services/assist-api/utils';

const QUERY_BATCH_SIZE = 1000;

export type BaseClusterResponse = {
  id: number;
  numberOfItems: number;
  rawScore: number;
  scaleError: number;
  scaleScore: number;
  stage: string;
  stanine: number;
  logitError: number;
  logitScore: number;
  status: ResponseStatus;
  testInstance: undefined; // Required only because ReportNavigator requires this in props.
  testInstanceUser?: ClusterTestInstanceUser;
  responseItems: ResponseItem[];
  responseCategories: ResponseCategory[];
};

export type ClusterTestInstanceUser = {
  id: number;
  level: { id: number };
  referenceLevel: { id: number };
  gender?: { id: number };
  ethnicities?: { id: number; name: string; dmu: number }[];
};

export type ResponseItem = {
  id: number;
  isCorrect: boolean;
  submittedOptions?: { id: number; identifier: string; description: string }[];
  testItem: { id: number; identifier: string };
};

const order = {};
const groups = [
  'id',
  'testinstanceresponse-details',
  'testinstanceresponse-status',
  'testinstanceresponse-user',
  'user-ethnicities',
  'user-gender',
  'testinstanceuser-level',
  'testinstanceuser-referencelevel',
  'testinstanceresponse-responseitems',
  'testinstanceresponse-responsecategories',
  'testinstanceresponseitem-testitem',
  'testinstanceresponseitem-submittedoptions',
  'testinstanceresponseitem-submittedvalue',
  'testinstanceresponseitem-iscorrect'
];

const include = '';

/**
 * Fetch test-instance-responses/report
 * @param params can be used to customise/limit the test-instance-response/report retrieved
 * @returns the requested test-instance-response/report
 */
export const getClusterResponses: GetMany<BaseClusterResponse, false> = async (params) =>
  getBatchedQueries(
    async () =>
      (
        await ASSIST_API.get('test-instance-responses/customer-group/list', {
          params: requestParams({}, { ...params, pagination: { itemsPerPage: 0, page: 1 } })
        })
      ).data.meta.totalItems,
    async (itemsPerPage, page) =>
      (
        await ASSIST_API.get('test-instance-responses/reports/customer-group', {
          params: requestParams(
            { order, groups, include, pagination: { itemsPerPage, page } },
            params
          )
        })
      ).data.map((json) => ({
        ...json,
        logitScore: +json.logitScore,
        scaleScore: +json.scaleScore,
        logitError: convertErrorMarginToNumber(json.logitError),
        scaleError: convertErrorMarginToNumber(json.scaleError),
        responseItems: json.responseItems || [],
        // flatten out the various user entities
        testInstanceUser: json.user && {
          ...json.user,
          ...json.user.user,
          ...json.user.user.user,
          id: json.user.id,
          groups: json.user.user.groups?.flatMap(({ group }) => group)
        }
      })),
    QUERY_BATCH_SIZE
  );

/**
 * Hook implementation to fetch test-instance-responses
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns test-instance-responses matching request
 */
export const useClusterResponses: AssistQuery.UseMany<BaseClusterResponse, false> = (
  params,
  options
) =>
  useQueryAssist(
    ['test-instance-responses', 'cluster', params],
    () => getClusterResponses(params),
    options
  );
